import PropTypes from "prop-types";
import { ChevronRight } from "lucide-react";
import { formatCalendarDay } from "./lib/format";
import { formatRatePct, formatSignedUsd } from "./lib/fund";
import { formatUsd } from "./lib/money";

/**
 * "How this is computed" (spec §5.4: reproducible by hand): each figure's
 * rule in plain words, worked with the scoreboard's own figures, so a
 * person with a calculator can check every one — including that the shadow
 * is never sold, so it carries no tax. The rules are the server's
 * (backend/investing/scoreboard.py); the figures are GET scoreboard/'s.
 */
function ScoreboardMethod({ board }) {
  const symbol = board.benchmark_symbol;
  const year = board.tax_year;
  return (
    <details className="group mt-3">
      <summary className="flex min-h-11 cursor-pointer items-center gap-1 text-sm font-medium md:min-h-8">
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
        />
        How this is computed
      </summary>
      <ul aria-label="How this is computed" className="mt-1 mb-0 flex list-disc flex-col gap-2 pl-5 text-sm">
        <li>
          <strong>Net contributions</strong>: deposits minus withdrawals dated on or before{" "}
          {formatCalendarDay(board.through)} — the snapshot&apos;s day in New York: {formatUsd(board.deposits)} −{" "}
          {formatUsd(board.withdrawals)} = {formatUsd(board.net_contributions)}.
        </li>
        <li>
          <strong>{symbol} shadow</strong>: the same money held in {symbol} instead. Each deposit buys {symbol} at its
          close that day, and each withdrawal sells it the same way; each {symbol} distribution is reinvested at the
          close on its ex-dividend day, earned only by the units held before that day. Units are rounded half up to 8
          places at each step. At this snapshot: {board.shadow_units} {symbol} units × {formatUsd(board.benchmark_price)}{" "}
          = {formatUsd(board.shadow_value)}. The shadow is never sold: it&apos;s unrealized, so it carries no tax.
        </li>
        <li>
          <strong>Account</strong>: the snapshot&apos;s total value — cash plus positions, as Robinhood reported them.
        </li>
        <li>
          <strong>Account − {symbol} shadow</strong>: {formatUsd(board.account_value)} − {formatUsd(board.shadow_value)}{" "}
          = {formatSignedUsd(board.difference)}.
        </li>
        <li>
          <strong>Simple returns</strong>: value ÷ net contributions − 1, rounded half up to 0.01%. They&apos;re not
          time-weighted: money added late counts as much as money there from the start, so they don&apos;t compare
          with a fund&apos;s published returns.
        </li>
        <li>
          <strong>Estimated tax for {year}</strong>: what was realized in {year} as Robinhood reports it: short-term{" "}
          {formatUsd(board.realized_short)}, long-term {formatUsd(board.realized_long)}. A loss in one offsets a gain in
          the other, and nothing carries over from other years, leaving {formatUsd(board.taxable_short)} short-term and{" "}
          {formatUsd(board.taxable_long)} long-term taxable: {formatUsd(board.taxable_short)} ×{" "}
          {formatRatePct(board.tax_rate_short)} + {formatUsd(board.taxable_long)} × {formatRatePct(board.tax_rate_long)}{" "}
          = {formatUsd(board.estimated_tax)} (your settings&apos; rates, rounded half up to the cent). Dividend tax
          isn&apos;t included. It&apos;s an estimate for the comparison, not tax advice.
        </li>
        <li>
          <strong>Account after estimated tax</strong>: {formatUsd(board.account_value)} −{" "}
          {formatUsd(board.estimated_tax)} = {formatUsd(board.account_after_tax)}.
        </li>
      </ul>
    </details>
  );
}

ScoreboardMethod.propTypes = {
  /** GET scoreboard/ (InvestingScoreboard). */
  board: PropTypes.shape({
    benchmark_symbol: PropTypes.string.isRequired,
    through: PropTypes.string.isRequired,
    tax_year: PropTypes.number.isRequired,
    deposits: PropTypes.string.isRequired,
    withdrawals: PropTypes.string.isRequired,
    net_contributions: PropTypes.string.isRequired,
    shadow_units: PropTypes.string.isRequired,
    benchmark_price: PropTypes.string.isRequired,
    shadow_value: PropTypes.string.isRequired,
    account_value: PropTypes.string.isRequired,
    difference: PropTypes.string.isRequired,
    realized_short: PropTypes.string.isRequired,
    realized_long: PropTypes.string.isRequired,
    taxable_short: PropTypes.string.isRequired,
    taxable_long: PropTypes.string.isRequired,
    tax_rate_short: PropTypes.string.isRequired,
    tax_rate_long: PropTypes.string.isRequired,
    estimated_tax: PropTypes.string.isRequired,
    account_after_tax: PropTypes.string.isRequired,
  }).isRequired,
};

export default ScoreboardMethod;
